import { PermissionRoles } from '@gearbelt/global';
import { CompanyPlanTypes, GearbeltPlanType, IAccount, IGetWorkspaceSwitcherResponseDto, Products } from '@site-mate/sitemate-global-shared';
import { SitemateRegions } from '@site-mate/sitemate-infra-shared/models';
import { WorkspaceUrlService } from '@site-mate/sitemate-infra-shared/services';

import DashpivotIcon from '@/assets/workspace-switcher/dashpivot-icon.svg';
import FlowsiteIcon from '@/assets/workspace-switcher/flowsite-icon.svg';
import GearbeltIcon from '@/assets/workspace-switcher/gearbelt-icon.svg';
import SitemateStartIcon from '@/assets/workspace-switcher/sitemate-start-icon.svg';
import { IPermissionDto } from '@/services/global-api/permission.service';
import { toInfraProduct } from '@/utils/product';

export const PRODUCT_ORDER: Products[] = [Products.Dashpivot, Products.Gearbelt, Products.Flowsite];

export const PRODUCT_ICONS: Partial<Record<Products, string>> = {
  [Products.SitemateStart]: SitemateStartIcon,
  [Products.Dashpivot]: DashpivotIcon,
  [Products.Gearbelt]: GearbeltIcon,
  [Products.Flowsite]: FlowsiteIcon,
};

export const AVATAR_COLORS = ['#4A90D9', '#7B61FF', '#2ECC71', '#E67E22', '#E74C3C', '#1ABC9C', '#E91E8F', '#5C6BC0'];

const workspaceUrlService = new WorkspaceUrlService();

export function getInitial(name: string): string {
  const firstAlphanumeric = name.match(/[a-zA-Z0-9]/);
  return firstAlphanumeric ? firstAlphanumeric[0].toUpperCase() : name.charAt(0).toUpperCase();
}

export function getAvatarColor(name: string): string {
  const firstAlphanumeric = name.match(/[a-zA-Z0-9]/);
  const charCode = (firstAlphanumeric ? firstAlphanumeric[0].codePointAt(0) : name.codePointAt(0)) || 0;
  return AVATAR_COLORS[charCode % AVATAR_COLORS.length];
}

export function generateWorkspaceUrl(workspace: {
  id: string;
  product: Products;
  legacyId?: string | null;
  region?: string | null;
  subdomain?: string | null;
}) {
  let url = workspaceUrlService.getWorkspaceUrl(window.location, {
    id: workspace.id,
    product: toInfraProduct(workspace.product),
    legacyId: workspace.legacyId ?? undefined,
    region: (workspace.region as SitemateRegions) ?? undefined,
    subdomain: workspace.subdomain ?? undefined,
  });

  if (workspace.product === Products.Gearbelt) {
    url = `${url}/dashboard`;
  }

  return url;
}

export function getCreateWorkspaceUrl(
  product: Products,
  accountId: string,
  urlService: WorkspaceUrlService,
  location: Pick<Location, 'hostname' | 'protocol' | 'port'>,
  account?: { region?: string | null; subdomain?: string | null },
): string {
  if (product === Products.Gearbelt) {
    return `/create-workspace?accountId=${accountId}`;
  }

  const url = urlService.getWorkspaceUrl(location, {
    id: 'placeholder',
    product: toInfraProduct(product),
    region: (account?.region as SitemateRegions) ?? undefined,
    subdomain: account?.subdomain ?? undefined,
  });
  if (!url) return '#';

  return `${new URL(url).origin}/create-workspace?accountId=${accountId}`;
}

export interface IAccountGroup {
  accountId: string;
  accountName: string;
  products: IProductGroup[];
}

export interface IProductGroup {
  name: Products;
  workspaces: IWorkspaceInfo[];
}

export interface IWorkspaceInfo {
  id: string;
  name: string;
  logo?: string;
  product: Products;
  legacyId?: string | null;
  region?: string | null;
  subdomain?: string | null;
}

export interface IProductCreatePermission {
  canCreate: boolean;
  isDisabled?: boolean;
  disabledMessage?: string;
}

export type IAccountCreatePermissions = Record<Products, IProductCreatePermission>;

const GEARBELT_QUALIFIED_PLANS: GearbeltPlanType[] = [GearbeltPlanType.Legacy, GearbeltPlanType.Pro, GearbeltPlanType.Premium, GearbeltPlanType.Platinum];
export const DASHPIVOT_CONTROLLER_ROLE = 'dashpivot-controller';

export const SITEMATE_SUPERUSER_ROLE = 'sitemate-superuser';
export const ACCOUNT_ADMIN_ROLE = 'account-admin';
export const ACCOUNT_LAYER_ROLES = [ACCOUNT_ADMIN_ROLE, SITEMATE_SUPERUSER_ROLE];

export function hasAccountLayerAccess(permissions: IPermissionDto[], accountId: string, isSuperUser: boolean): boolean {
  if (isSuperUser) return true;
  return permissions.some((p) => p.role === SITEMATE_SUPERUSER_ROLE || (p.role === ACCOUNT_ADMIN_ROLE && p.target === `/${accountId}`));
}

export function resolveAccountCreatePermissions(
  accountId: string,
  account: IAccount | null | undefined,
  permissions: IPermissionDto[],
  isSuperUser: boolean,
  workspaceIds: string[] = [],
): IAccountCreatePermissions {
  const hasDashpivotOrgControllerRole = permissions.some((p) => p.role === DASHPIVOT_CONTROLLER_ROLE && workspaceIds.some((wsId) => p.target === `/${wsId}`));
  const hasGearbeltAdminInAccount = permissions.some((p) => p.role === PermissionRoles.GearbeltAdmin && workspaceIds.some((wsId) => p.target === `/${wsId}`));

  const gearbeltPlanType = account?.billing?.gearbelt?.planType;
  const accountRegion = account?.region;
  const isUkRegion = accountRegion === 'uk1';

  const gearbeltPlanQualified = gearbeltPlanType ? GEARBELT_QUALIFIED_PLANS.includes(gearbeltPlanType) : false;

  const dashpivot: IProductCreatePermission = (() => {
    if (isSuperUser) return { canCreate: true };
    if (!hasDashpivotOrgControllerRole) return { canCreate: false };

    const dashpivotPlanType = account?.billing?.dashpivot?.planType;

    if (dashpivotPlanType === CompanyPlanTypes.FreeTrial) {
      return { canCreate: true, isDisabled: true, disabledMessage: 'Creating additional Dashpivot workspaces is not included in our Free Trial' };
    }

    if (dashpivotPlanType === CompanyPlanTypes.Standard) {
      return { canCreate: true, isDisabled: true, disabledMessage: 'Creating additional Dashpivot workspaces is not included on the Standard Plan' };
    }

    return { canCreate: true };
  })();

  const gearbelt: IProductCreatePermission = (() => {
    if (isSuperUser) return { canCreate: true };
    if (isUkRegion) return { canCreate: false };
    if (!hasGearbeltAdminInAccount) return { canCreate: false };

    if (gearbeltPlanType === GearbeltPlanType.FreeTrial) {
      return { canCreate: true, isDisabled: true, disabledMessage: 'Creating additional Gearbelt workspaces is not included in our Free Trial' };
    }

    if (gearbeltPlanType === GearbeltPlanType.Standard) {
      return { canCreate: true, isDisabled: true, disabledMessage: 'Creating additional Gearbelt workspaces is not included on the Standard Plan' };
    }

    return { canCreate: gearbeltPlanQualified };
  })();

  const flowsite: IProductCreatePermission = {
    canCreate: isSuperUser,
  };

  return {
    [Products.Dashpivot]: dashpivot,
    [Products.Gearbelt]: gearbelt,
    [Products.Flowsite]: flowsite,
  } as IAccountCreatePermissions;
}

export function groupWorkspacesIntoAccounts(workspaces: IGetWorkspaceSwitcherResponseDto[]): IAccountGroup[] {
  const accountMap = new Map<string, { accountName: string; workspacesByProduct: Map<Products, IWorkspaceInfo[]> }>();

  for (const ws of workspaces) {
    if (!accountMap.has(ws.accountId)) {
      accountMap.set(ws.accountId, { accountName: ws.accountName, workspacesByProduct: new Map() });
    }

    const account = accountMap.get(ws.accountId);
    if (!account) continue;

    if (!account.workspacesByProduct.has(ws.product)) {
      account.workspacesByProduct.set(ws.product, []);
    }

    const productWorkspaces = account.workspacesByProduct.get(ws.product);
    if (!productWorkspaces) continue;

    productWorkspaces.push({
      id: ws.workspaceId,
      name: ws.name,
      logo: ws.logo,
      product: ws.product,
      legacyId: ws.legacyId,
      region: ws.region,
      subdomain: ws.subdomain,
    });
  }

  return Array.from(accountMap.entries())
    .map(([accountId, { accountName, workspacesByProduct }]) => ({
      accountId,
      accountName,
      products: PRODUCT_ORDER.filter((product) => workspacesByProduct.has(product)).map((product) => ({
        name: product,
        workspaces: (workspacesByProduct.get(product) ?? []).sort((a, b) => a.name.localeCompare(b.name)),
      })),
    }))
    .sort((a, b) => a.accountName.localeCompare(b.accountName));
}
